import { MuiButton } from '@/components/button/MuiButton';
import { useLayoutFooter } from '@/hooks/useLayoutFooter';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { HOSPITAL_SEARCH_FORM_MODE } from '../HospitalsSearchForm/logic';
import { LimitedFreeTooltip } from '@/components/tooltip/LimitedFreeTooltip';
import { LimitedFreeIcon } from '@/features/icon/LimitedFreeIcon';

type BaseInfoProps = {
  excludeRequested: boolean;
  onToggleExcludeRequested: (checked: boolean) => void;
};

export type MatchInfoProps = {
  key: 'match';
  value: number;
} & BaseInfoProps;

export type SelectInfoProps = {
  key: 'select';
  value: number;
  onMultiRequest: () => void;
} & BaseInfoProps;

type Props = {
  mode: HOSPITAL_SEARCH_FORM_MODE;
  matchInfo: MatchInfoProps;
  selectInfo: SelectInfoProps;
  changeMode: (mode: HOSPITAL_SEARCH_FORM_MODE) => void;
};

const initialHeight = 36;

export function HospitalMultiRequestFooter({
  mode,
  matchInfo,
  selectInfo,
  changeMode,
}: Props) {
  const { footerHeight } = useLayoutFooter();

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: footerHeight || initialHeight,
        height: '56px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        bgcolor: '#F5FAF9',
        padding: '0 120px',
        flexWrap: 'wrap',
        zIndex: 1200,
        '@media (max-width: 900px)': {
          columnGap: 0,
          height: '120px',
          px: 3,
        },
      }}
    >
      {mode === 'search' && (
        <Content
          title="紹介可能な医療機関"
          value={matchInfo.value}
          info={matchInfo}
        >
          <LimitedFreeTooltip>
            <MuiButton
              variant="contained"
              onClick={() => {
                changeMode('multiRequest');
              }}
              disabled={matchInfo.value === 0}
              endIcon={<LimitedFreeIcon />}
            >
              すべての医療機関を一括選択
            </MuiButton>
          </LimitedFreeTooltip>
        </Content>
      )}

      {mode === 'multiRequest' && (
        <Content
          title="選択中の医療機関"
          value={selectInfo.value}
          info={selectInfo}
        >
          <MuiButton
            variant="contained"
            onClick={() => selectInfo.onMultiRequest()}
            disabled={selectInfo.value === 0}
          >
            紹介カードの作成へ進む
          </MuiButton>
        </Content>
      )}
    </Box>
  );
}

function Content({
  title,
  value,
  children,
  info,
}: {
  title: string;
  value: number;
  children: React.ReactNode;
  info: MatchInfoProps | SelectInfoProps;
}) {
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="JP/Body/m/Medium/Tight"
          sx={{
            color: '#262626',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="JP/Body/m/Medium/Tight"
          sx={{
            color: '#262626',
          }}
        >{`${value}件`}</Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <FormGroup
          sx={{
            backgroundColor: 'bg.lighter',
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            label={'紹介中の医療機関を除外する'}
            style={{
              marginLeft: '0rem',
              marginRight: 2,
            }}
            control={
              <Checkbox
                checked={info.excludeRequested}
                onChange={(_, checked) => {
                  info.onToggleExcludeRequested(checked);
                }}
                name={'紹介中の医療機関を除外する'}
                sx={{ padding: '2px 4px 0 0' }}
              />
            }
          />
        </FormGroup>
        {children}
      </Box>
    </>
  );
}
